import React, { Fragment, useEffect } from 'react'
import classNames from 'classnames'
import { useTable, useFilters, usePagination, useSortBy, useRowSelect } from 'react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import { faMeh } from '@fortawesome/pro-regular-svg-icons/faMeh'

import Filters from './Filters/index'
import Pagination from './Pagination/index'
import RowCountPicker from './RowCountPicker'
import ColumnSelector from './ColumnSelector/index'
import EditRows from './EditRows'

import { ActionPanel } from '@peracto/peracto-ui'

import * as S from './styled'

// react-table's prop getters include a `key`, which React 19 won't accept inside a spread
const withoutKey = ({ key, ...props }) => props

const renderHeader = ({ headers }) => (
    <tr>
        {headers.map((header, i) => (
            <Fragment key={i}>
                {header.show && (
                    <th
                        {...withoutKey(header.getHeaderProps(header.getSortByToggleProps()))}
                        data-testid={`header-${i}`}
                    >
                        {header.render('Header')}
                        {header.isSorted ? (
                            header.isSortedDesc ? (
                                <FontAwesomeIcon icon={faChevronDown} className="!ml-2" />
                            ) : (
                                <FontAwesomeIcon icon={faChevronUp} className="!ml-2" />
                            )
                        ) : (
                            ''
                        )}
                    </th>
                )}
            </Fragment>
        ))}
    </tr>
)

const renderRow = (row, i) => {
    return (
        <tr
            {...withoutKey(row.getRowProps())}
            className={classNames({
                selected: row.isSelected,
            })}
            key={`row_${i}`}
        >
            {row.cells.map((cell, i) => (
                <td
                    key={cell.getCellProps().key ?? i}
                    {...withoutKey(cell.getCellProps())}
                    data-testid={
                        typeof cell.row.cells[i].column.Header === 'string'
                            ? `row-${cell.row.index}-${cell.row.cells[i].column.Header.toString()
                                  .toLowerCase()
                                  .replaceAll(' ', '-')}`
                            : null
                    }
                >
                    {cell.render('Cell')}
                </td>
            ))}
        </tr>
    )
}

const renderRows = ({ page, prepareRow }) => {
    return page && page.length
        ? page.map((row, i) => {
              prepareRow(row)
              return renderRow(row, i)
          })
        : null
}

const defaults = {
    manualSorting: true, // Manual sorting
    manualFilters: true, // Manual filters
    manualPagination: true, // Manual pagination
    disableMultiSort: true, // Disable multi-sort
    disableGrouping: true, // Disable grouping
    disablePageResetOnDataChange: true,
    autoResetSortBy: false,
}

const Table = ({
    className = 'table',
    renderColumnSelector = (instance, props) => <ColumnSelector instance={instance} {...props} />,
    renderFilters = (instance, props) => <Filters instance={instance} {...props} />,
    renderPagination = (instance, props) => <Pagination instance={instance} {...props} />,
    renderRowCountPicker = (instance, props) => <RowCountPicker instance={instance} {...props} />,
    fetchData,
    state,
    currentFilters = {},
    loading,
    noResultsMessage,
    fixedRowCount,
    showPagination = true,
    actionPanelContent = null,
    allowDuplicate = false,
    ...props
}) => {
    const [{ sortBy, pageIndex, pageSize, selectedRows }] = state

    const ActionPanelContent = actionPanelContent || EditRows

    useEffect(() => {
        fetchData(state[0])
    }, [sortBy, pageIndex, pageSize])

    const instance = useTable(
        {
            ...defaults,
            ...props,
            state,
            manualSorting: true,
            currentFilters,
            getRowID: (row, index) => {
                return row.hasOwnProperty('originId')
                    ? row.originId
                    : row?.id
                    ? row.id
                    : pageIndex * pageSize + index
            },
        },
        useFilters,
        useSortBy,
        usePagination,
        useRowSelect
    )

    const hasFiltersApplied = () => {
        const filterValues = Object.values(currentFilters)
        return filterValues.filter(val => val && val.length > 0).length > 0
    }

    return (
        <>
            {renderFilters(instance, props)}
            {!loading && instance.numberOfRows === 0 ? (
                <S.NoResultsContainer>
                    {noResultsMessage ? (
                        noResultsMessage
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faMeh} size="6x" className="!mb-4" />
                            <p>
                                No results found {hasFiltersApplied && ' for the filters applied'}
                            </p>
                        </>
                    )}
                </S.NoResultsContainer>
            ) : (
                <>
                    <S.TableContainer>
                        <table {...instance.getTableProps()} className={className}>
                            <thead>{renderHeader(instance)}</thead>
                            <tbody>{renderRows(instance)}</tbody>
                        </table>
                        <ActionPanel isVisible={selectedRows.length > 0}>
                            <ActionPanelContent
                                instance={instance}
                                selectedRows={selectedRows}
                                reloadData={() => fetchData(state[0])}
                                allowDuplicate={allowDuplicate}
                            />
                        </ActionPanel>
                    </S.TableContainer>
                    <div className="!flex !flex-col md:!flex-row !items-center !justify-between">
                        {renderColumnSelector(instance, props)}
                        {showPagination && renderPagination(instance, props)}
                        {!!fixedRowCount === false && showPagination ? (
                            renderRowCountPicker(instance, props)
                        ) : (
                            <div></div>
                        )}
                    </div>
                </>
            )}
        </>
    )
}

export default Table
